import { useMemo } from 'react';

import AddchartOutlined from '@mui/icons-material/AddchartOutlined';

import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import Launch from '@mui/icons-material/Launch';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import { List } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import type { NavigationItemData } from '@/components/NavBar/components/NavigationItem';
import NavigationItem from '@/components/NavBar/components/NavigationItem';
import { useDashboardMenuItems } from '@/components/NavBar/hooks/useDashboardMenuItems';
import { useMarketsNavigationItems } from '@/components/NavBar/hooks/useMarketsNavigationItems';
import {
  FWDCURVE_CATALOG_PATH,
  METADATA_FIRST_NAME_FIELD,
  METADATA_LAST_NAME_FIELD,
  SPOT_CATALOG_PATH
} from '@/constants/constants';
import { DATA_SCIENCE_PROTOTYPES_FLAG, PERSONALIZATION_FLAG } from '@/constants/featureFlags';
import { DASHBOARD_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { dataHubRoutes } from '@/features/DataHub/constants/routes';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useOpenExportSelection, { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
import { openExportDrawer } from '@/slices/drawerSlice';
import { retrieveFromUserMetadata } from '@/utils/retrieveFromUserMetadata';

const NavBar = () => {
  const dispatch = useAppDispatch();
  const { userEmail, metadata } = useAppSelector(state => state.tripToken);
  const isPersonalizationEnabled = useFeatureFlagEnabled(PERSONALIZATION_FLAG);
  const isPrototypesEnabled = useFeatureFlagEnabled(DATA_SCIENCE_PROTOTYPES_FLAG);
  const { firstName, lastName } = useMemo(() => {
    return {
      firstName: retrieveFromUserMetadata(metadata || [], METADATA_FIRST_NAME_FIELD),
      lastName: retrieveFromUserMetadata(metadata || [], METADATA_LAST_NAME_FIELD)
    };
  }, [metadata]);
  const { menuItems: marketMenuItems } = useMarketsNavigationItems();
  const dashboardMenuItems = useDashboardMenuItems();
  const openExportSelection = useOpenExportSelection(ExportSelectionSource.NAV_BAR);

  const userName = useMemo(() => {
    if (!!firstName || !!lastName) {
      return (firstName ? `${firstName} ` : '') + lastName;
    }
    return userEmail;
  }, [firstName, lastName, userEmail]);

  const profileItems: NavigationItemData[] = [
    {
      key: 'settings',
      name: 'Settings',
      path: '/profile'
    },
    {
      key: 'logout',
      name: 'Logout',
      path: '/logout'
    }
  ];

  const dataHubItems: NavigationItemData[] = [
    {
      key: 'export',
      name: 'Export selection',
      onClick: openExportSelection
    },
    {
      key: 'past-exports',
      name: 'Past exports',
      onClick: () => dispatch(openExportDrawer({ showHistory: true }))
    }
  ];

  const catalogueItems: NavigationItemData[] = [
    {
      key: 'spot',
      name: 'Spot',
      path: `/catalog/${SPOT_CATALOG_PATH}`
    },
    {
      key: 'fwdCurve',
      name: 'Forward Curves',
      path: `/catalog/${FWDCURVE_CATALOG_PATH}`
    }
  ];

  const navigationBarItems: NavigationItemData[] = [
    {
      key: 'markets',
      name: 'Markets',
      icon: <DashboardOutlined />,
      path: '/markets',
      children: marketMenuItems
    },
    {
      key: 'catalog',
      name: 'Catalog',
      icon: <ListAltOutlined />,
      path: '/catalog',
      children: catalogueItems
    },
    {
      key: 'export',
      name: 'Export',
      icon: <Launch />,
      path: dataHubRoutes.base,
      children: dataHubItems
    },
    {
      key: 'profile',
      name: userName ?? '',
      icon: <PersonOutlined />,
      path: '/profile',
      children: profileItems
    }
  ];
  if (isPrototypesEnabled) {
    const profileIndex = navigationBarItems.findIndex(item => item.key === 'profile');
    navigationBarItems.splice(profileIndex, 0, {
      key: 'prototypes',
      name: 'Prototypes',
      icon: <ScienceOutlined />,
      path: '/prototypes'
    });
  }
  if (isPersonalizationEnabled) {
    navigationBarItems.unshift({
      key: 'dashboard',
      name: DASHBOARD_DEFAULT_BASE_NAME,
      icon: <AddchartOutlined />,
      path: '/dashboard',
      tag: 'beta',
      children: dashboardMenuItems
    });
  }
  return (
    <List
      component='nav'
      sx={{
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'y',
        maxHeight: 'calc(100vh - 88px)',
        flex: 1
      }}
    >
      {navigationBarItems.map(({ name, icon, path, key, children, tag }) => {
        return (
          <NavigationItem
            key={key}
            item={{
              key,
              name,
              icon,
              path,
              children,
              tag
            }}
          />
        );
      })}
    </List>
  );
};

export default NavBar;
